import { Arcade, ArcadeRoom } from "@arcade/sdk";

export const arcadeEndpoint = import.meta.env.VITE_ARCADE_ENDPOINT;

export const arcadeConfigured = Boolean(arcadeEndpoint);

// One Arcade client per gameId: identity and the WebSocket protocol are scoped to a game.
const clients = new Map<string, Promise<Arcade>>();

export async function getArcade(gameId: string, displayName?: string, avatarHash?: string): Promise<Arcade> {
  if (!arcadeConfigured) throw new Error("Turnstyle’s game server is not configured yet.");
  let existing = clients.get(gameId);
  if (!existing) {
    existing = (async () => {
      const client = new Arcade({ gameId, endpoint: arcadeEndpoint, displayName, avatarHash });
      await client.ready();
      return client;
    })();
    clients.set(gameId, existing);
  }
  const client = await existing;
  // Only sync when we hold the full profile. Syncing a displayName without the avatar hash
  // makes the server overwrite the stored avatar_hash with null, wiping the player's avatar.
  if (displayName && avatarHash) await client.syncProfile(displayName, avatarHash);
  return client;
}

export async function createPrivateGame(gameId: string, displayName?: string, playerCount?: number, avatarHash?: string): Promise<ArcadeRoom> {
  const arcade = await getArcade(gameId, displayName, avatarHash);
  return arcade.createRoom({ mode: "casual", visibility: "private", kind: "turn", playerCount });
}

export async function joinPrivateGame(gameId: string | undefined, code: string, displayName?: string, avatarHash?: string): Promise<ArcadeRoom> {
  const normalized = code.replace(/\s/g, "").toUpperCase();
  const inferred = normalized[0] === "O" ? "othello" : normalized[0] === "R" ? "gin-rummy" : normalized[0] === "A" ? "acquire" : normalized[0] === "L" ? "luna-genesis" : normalized.length > 7 ? normalized.slice(0, -7).replace(/-$/, "").toLowerCase() : undefined;
  const arcade = await getArcade(inferred ?? gameId ?? "", displayName, avatarHash);
  return arcade.joinRoom(normalized, gameId);
}

export async function deleteGame(gameId: string, roomId: string, roomToken: string, displayName?: string, avatarHash?: string): Promise<void> {
  const arcade = await getArcade(gameId, displayName, avatarHash);
  await arcade.deleteRoom(roomId, roomToken);
}

export async function roomParticipants(gameId: string, roomId: string, roomToken: string, displayName?: string, avatarHash?: string) {
  return (await getArcade(gameId, displayName, avatarHash)).getRoomParticipants(roomId, roomToken);
}

/**
 * Reconnect to an existing room from persisted credentials. The ArcadeRoom socket is built
 * from `endpoint` + roomId + token, so the original `wsUrl` is not needed on resume.
 */
export async function connectRoom(gameId: string, roomId: string, token: string): Promise<ArcadeRoom> {
  await getArcade(gameId); // ensure identity is minted before opening the socket
  return new ArcadeRoom({ roomId, token, wsUrl: "" }, arcadeEndpoint);
}
